/**
 * CustomerPendingTickets (TICKET-01c v2) — the customer's open pending tickets
 * as FIRST-CLASS cards in the Overview's dispatch flow, right where the
 * adaptive banner lives. Per Seth's two-window model: the dispatcher working
 * this customer is IN the 360 (the board is ambient on the other monitor), so
 * details expand IN PLACE — no jumping to the board. Replaces the header chip
 * (v1) that was "definitely going to be lost up there."
 *
 * "Schedule" opens the full Schedule window (CreateDispatchModal) in booking
 * mode and books THIS ticket onto the board in place — no duplicate (Seth
 * 2026-06-24: one scheduling surface; the old inline suggest panel retired).
 */
import { useState } from "react";
import {
  Package,
  RotateCcw,
  Hourglass,
  ChevronDown,
  ChevronUp,
  Check,
  CalendarPlus,
  CalendarClock,
  Trash2,
  Pencil,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { DispatchLink } from "@/components/links";
import { useActiveTechs } from "@/features/board/api";
import {
  usePendingForCustomer,
  useUpdateTicket,
  useResolveTicket,
  ageLabel,
  PART_STATE_LABEL,
  SOURCE_LABEL,
  partReady,
  type PendingTicket,
} from "@/features/board/queue";
import { CreateDispatchModal } from "./CreateDispatchModal";

/** FIELD-M0: open a tech's part photo (data plate etc.) from job-media. */
async function openPartPhoto(path: string) {
  const { data } = await supabase.storage.from("job-media").createSignedUrl(path, 3600);
  if (data?.signedUrl) window.open(data.signedUrl, "_blank", "noopener");
}

const LANE_TITLE: Record<string, string> = {
  incoming: "Request waiting to be scheduled",
  order_parts: "Part ticket",
  return_visit: "Return visit to book",
  sale_visit: "Finalize-sale visit to book",
  reschedule: "Needs a new date",
  weather_hold: "On weather hold",
  campaign: "Visit to schedule",
};

/** Short lane label chip — so a dispatcher always knows WHAT they're looking
 * at (Seth: "I don't want my dispatchers confused about what this is"). */
const LANE_CHIP: Record<string, string> = {
  incoming: "Incoming request",
  order_parts: "Parts",
  return_visit: "Return visit",
  sale_visit: "Finalize sale",
  reschedule: "Reschedule",
  weather_hold: "Weather hold",
  campaign: "Agreement campaign",
};

const prettyDate = (d: string | null) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "";

export function CustomerPendingTickets({ custno }: { custno: string }) {
  const all = usePendingForCustomer(custno).data ?? [];
  // Missed calls are a DISPATCHER work item (the strip's Missed-calls lane),
  // not a job-in-waiting on the customer — showing them here read as clutter
  // (Seth 2026-06-10). The call itself is already on the Messages timeline,
  // which is the long-term record.
  const tickets = all.filter((t) => t.lane_key !== "missed_call");
  if (tickets.length === 0) return null;
  return (
    <div className="space-y-3">
      {tickets.map((t) => (
        <PendingCard key={t.id} t={t} />
      ))}
    </div>
  );
}

function PendingCard({ t }: { t: PendingTicket }) {
  const [open, setOpen] = useState(false);
  const update = useUpdateTicket();
  const resolve = useResolveTicket();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const techs = useActiveTechs("03").data ?? [];
  const techName = (empno: string | null) => {
    if (!empno) return null;
    const x = techs.find((tt) => tt.empno === empno);
    return (
      x?.empname ?? (`${x?.firstname ?? ""} ${x?.lastname ?? ""}`.trim() || empno)
    );
  };

  const isParts = t.lane_key === "order_parts";
  const arrived = isParts && partReady(t.part_state);
  const Icon =
    t.lane_key === "order_parts"
      ? Package
      : t.lane_key === "return_visit"
        ? RotateCcw
        : Hourglass;
  const title = arrived
    ? t.part_state === "in_stock"
      ? "Part in stock — book the return visit"
      : "Part arrived — book the return visit"
    : t.lane_key === "campaign" && /tune-?up|check/i.test(t.summary)
      ? "Agreement tune-up to schedule"
      : (LANE_TITLE[t.lane_key] ?? "Pending ticket");

  return (
    <Card
      className={cn(
        "overflow-hidden border-l-4",
        arrived ? "border-l-emerald-500" : "border-l-amber-400",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
      >
        <span
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
            arrived
              ? "bg-emerald-50 text-emerald-700"
              : "bg-amber-50 text-amber-700",
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            {title}
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium normal-case text-slate-500">
              {LANE_CHIP[t.lane_key] ?? t.lane_key}
            </span>
          </span>
          <span className="block truncate text-xs text-slate-500">
            {t.summary}
            {isParts && t.part_state && !arrived
              ? ` · ${PART_STATE_LABEL[t.part_state]}`
              : ""}
            {t.affinity_empno ? ` · ${techName(t.affinity_empno)} goes back` : ""}
          </span>
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="border-t border-hairline px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            {isParts && t.part_state && (
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 font-semibold",
                  t.part_state === "needs_ordering" && "bg-red-50 text-red-700",
                  t.part_state === "ordered" && "bg-slate-100 text-slate-600",
                  t.part_state === "backordered" && "bg-amber-50 text-amber-700",
                  (t.part_state === "arrived" || t.part_state === "in_stock") && "bg-emerald-50 text-emerald-700",
                )}
              >
                {PART_STATE_LABEL[t.part_state]}
              </span>
            )}
            {isParts && t.part_po && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-500" title={t.part_eta ? `ETA ${t.part_eta}` : "On this purchase order"}>
                PO {t.part_po}
              </span>
            )}
            {isParts && (t.part_photos ?? []).length > 0 && (t.part_photos ?? []).map((p, i) => (
              <button
                key={p}
                type="button"
                onClick={() => void openPartPhoto(p)}
                title="Open the tech's photo (data plate / part)"
                className="rounded bg-brand-50 px-1.5 py-0.5 font-semibold text-brand-700 transition-colors hover:bg-brand-100"
              >
                📷 Photo {i + 1}
              </button>
            ))}
            {isParts && t.part_equipment && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600" title="The unit this part is for">
                🔧 {[t.part_equipment.mfg, t.part_equipment.model].filter(Boolean).join(" ") || t.part_equipment.eq_type || "unit on file"}{t.part_equipment.serial ? ` · S/N ${t.part_equipment.serial}` : ""}
              </span>
            )}
            {t.payment_status && (
              <span
                className={cn(
                  "rounded px-1.5 py-0.5 font-semibold",
                  t.payment_status === "prepaid"
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-amber-50 text-amber-700",
                )}
              >
                {t.payment_status === "prepaid" ? "Prepaid" : "COD"}
              </span>
            )}
            {t.quoted_amount != null && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">
                ${Math.round(Number(t.quoted_amount)).toLocaleString()} quoted
              </span>
            )}
            {t.est_install_minutes != null && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">
                ~{t.est_install_minutes} min install
              </span>
            )}
            {t.part_number && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">
                part # {t.part_number}
              </span>
            )}
            {(t.affinity_empno || t.target_date || t.target_window) && (
              <span className="inline-flex items-center gap-1 rounded bg-brand-50 px-1.5 py-0.5 font-semibold text-brand-700" title="Targeted tech + time frame (not booked yet)">
                <CalendarClock className="h-3 w-3" />
                {t.affinity_empno ? techName(t.affinity_empno) : "Unassigned"}
                {t.target_date ? ` · ${prettyDate(t.target_date)}` : ""}
                {t.target_window ? ` · ${t.target_window}` : ""}
              </span>
            )}
          </div>

          <p className="mt-2 text-[11px] text-slate-500">
            {ageLabel(t.created_at)} · {SOURCE_LABEL[t.source] ?? t.source}
            {t.origin_dispatch && (
              <>
                {" · from dispatch "}
                <DispatchLink dispatch={t.origin_dispatch} />
              </>
            )}
            {t.ordered_by
              ? ` · ordered by ${(t.ordered_by ?? "").split("@")[0]}`
              : ""}
            {t.arrived_by
              ? ` · received by ${(t.arrived_by ?? "").split("@")[0]}`
              : ""}
            {t.scheduled_dispatch && (
              <>
                {" · dispatch "}
                <DispatchLink dispatch={t.scheduled_dispatch} />
              </>
            )}
            {" · ref "}
            <span className="font-mono text-slate-400">{t.id.slice(0, 8)}</span>
          </p>
          <div className="mt-1.5">
            {editingNotes ? (
              <div className="space-y-1.5">
                <textarea
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={3}
                  autoFocus
                  placeholder="Notes for this ticket…"
                  className="w-full rounded-md border border-hairline px-2 py-1.5 text-xs text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => { update.mutate({ id: t.id, notes: noteDraft.trim() || null }); setEditingNotes(false); }}
                    className="rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand-800"
                  >
                    Save notes
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingNotes(false)}
                    className="rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-1.5">
                <p className="flex-1 text-xs leading-snug text-slate-600">
                  {t.notes || <span className="italic text-slate-400">No notes yet</span>}
                </p>
                <button
                  type="button"
                  onClick={() => { setNoteDraft(t.notes ?? ""); setEditingNotes(true); }}
                  title="Edit notes"
                  className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {isParts && t.part_state === "needs_ordering" && (
              <>
                <button
                  type="button"
                  onClick={() => update.mutate({ id: t.id, part_state: "ordered" })}
                  className="rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >
                  Mark ordered
                </button>
                <button
                  type="button"
                  onClick={() => update.mutate({ id: t.id, part_state: "in_stock" })}
                  title="We already have it on the shelf — no ordering needed"
                  className="rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >
                  In stock
                </button>
              </>
            )}
            {isParts && t.part_state === "ordered" && (
              <>
                <button
                  type="button"
                  onClick={() => update.mutate({ id: t.id, part_state: "arrived" })}
                  className="rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >
                  Part arrived
                </button>
                <button
                  type="button"
                  onClick={() => update.mutate({ id: t.id, part_state: "backordered" })}
                  title="Vendor can't fill it yet — flags the wait honestly"
                  className="rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-50"
                >
                  Backordered
                </button>
              </>
            )}
            {isParts && t.part_state === "backordered" && (
              <button
                type="button"
                onClick={() => update.mutate({ id: t.id, part_state: "arrived" })}
                className="rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
              >
                Part arrived
              </button>
            )}
            {(!isParts || arrived) && (
              <>
                <button
                  type="button"
                  onClick={() => setScheduleOpen(true)}
                  title="Open the Schedule window — assign a tech, day and arrival window, and place it on the board"
                  className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand-800"
                >
                  <CalendarPlus className="h-3 w-3" />
                  Schedule
                </button>
                <button
                  type="button"
                  onClick={() =>
                    resolve.mutate({ id: t.id, status: "scheduled" })
                  }
                  title="Already booked this in ESC — just clear the ticket from the queue"
                  className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50"
                >
                  <Check className="h-3 w-3" />
                  Booked in ESC
                </button>
              </>
            )}
            <div className="ml-auto">
              {confirmRemove ? (
                <span className="inline-flex items-center gap-1.5 text-[11px]">
                  <span className="text-slate-500">Remove this?</span>
                  <button
                    type="button"
                    onClick={() => resolve.mutate({ id: t.id, status: "cancelled" })}
                    className="rounded-md bg-red-600 px-2 py-1 font-semibold text-white transition-colors hover:bg-red-700"
                  >
                    Yes, remove
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmRemove(false)}
                    className="rounded-md border border-hairline bg-white px-2 py-1 font-medium text-slate-500 transition-colors hover:bg-slate-50"
                  >
                    Keep
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmRemove(true)}
                  title="Remove this request from the queue"
                  className="inline-flex items-center gap-1 rounded-md border border-hairline bg-white px-2 py-1 text-xs font-medium text-slate-400 transition-colors hover:border-red-200 hover:text-red-600"
                >
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              )}
            </div>
          </div>
          {scheduleOpen && (
            <CreateDispatchModal
              existingTicket={t}
              custno={t.custno}
              locno={t.locno}
              customerName={t.customer_name}
              address={t.address}
              city={t.city}
              phone={t.phone}
              onClose={() => setScheduleOpen(false)}
            />
          )}
        </div>
      )}
    </Card>
  );
}
